import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import logo from "../../../images/logo.svg";
import { ResetNewPassword } from "../../../environment";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";
type PasswordField = "password";

const ResetPassword = () => {
  const routes = all_routes;
  const location = useLocation();
  const navigate = useNavigate();

  const resetToken = location.state?.resetToken;

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const setNewPasswordAPI = useApi(ResetNewPassword, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleResetPassword = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();

    try {
      if (!email.trim()) {
        return Swal.fire({
          icon: "warning",
          title: "Email Required",
        });
      }

      if (!newPassword.trim()) {
        return Swal.fire({
          icon: "warning",
          title: "Password Required",
        });
      }

      if (newPassword !== confirmPassword) {
        return Swal.fire({
          icon: "error",
          title: "Passwords do not match",
        });
      }

      const payload = {
        email,
        newPassword,
        resetToken: resetToken,
      };

      const response = await setNewPasswordAPI.callApi(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Password Changed Successfully",
          text: "Your password has been updated. Please login with your new password.",
          confirmButtonText: "Back to Login",
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then((result) => {
          if (result.isConfirmed) {
            navigate(routes.login);
          }
        });
      } else {
        Swal.fire({
          icon: "error",
          title: response?.error,
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Something went wrong",
      });
    }
  };

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });
  const [password, setPassword] = useState("");
  const [passwordResponce, setPasswordResponce] = useState({
    passwordResponceText:
      "Use 8 or more characters with a mix of letters, numbers, and symbols.",
    passwordResponceKey: "",
  });

  const togglePasswordVisibility = (field: "password" | "confirmPassword") => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const onChangePassword = (password: string) => {
    setPassword(password);
    if (password.match(/^$|\s+/)) {
      setPasswordResponce({
        passwordResponceText: "",
        passwordResponceKey: "",
      });
    } else if (password.length === 0) {
      setPasswordResponce({
        passwordResponceText: "",
        passwordResponceKey: "",
      });
    } else if (password.length < 8) {
      setPasswordResponce({
        passwordResponceText: "Weak. Must contain at least 12 characters",
        passwordResponceKey: "0",
      });
    } else if (
      password.search(/[a-z]/) < 0 ||
      password.search(/[A-Z]/) < 0 ||
      password.search(/[0-9]/) < 0
    ) {
      setPasswordResponce({
        passwordResponceText:
          "Average. Must contain at least 1 upper case and number",
        passwordResponceKey: "1",
      });
    } else if (password.search(/(?=.*?[#?!@$%^&*-])/) < 0) {
      setPasswordResponce({
        passwordResponceText: "Almost. Must contain a special symbol",
        passwordResponceKey: "2",
      });
    } else {
      setPasswordResponce({
        passwordResponceText: "Awesome! You have a secure password.",
        passwordResponceKey: "3",
      });
    }
  };

  return (
    <>
      <div className="container-fluid p-0">
        <div className="row g-0 vh-100">
          {/* RIGHT SIDE - CONTENT + SCROLLING IMAGES */}
          <div className="col-lg-7 d-none d-lg-block">
            <div
              className="vh-100 position-relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              }}
            >
              {/* CONTENT */}
              <div
                className="position-absolute top-50 start-50 translate-middle text-center text-white px-2"
                style={{ zIndex: 2 }}
              >
                <img
                  src={logo}
                  alt="logo"
                  className="img-fluid mb-4"
                  style={{ maxWidth: "220px" }}
                />

                <h1
                  className="fw-bold mb-4"
                  style={{ fontSize: "28px", color: "white" }}
                >
                  Welcome to the Aupterz Portal
                </h1>

                <p
                  className="text-light"
                  style={{ fontSize: "18px", lineHeight: "32px" }}
                >
                  Sign in to access your background verification application,
                  upload required documents, track verification progress, and
                  complete your onboarding seamlessly.
                </p>
              </div>

              {/* SCROLLING IMAGES */}
              <div className="scroll-images">
                <img
                  src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f"
                  alt=""
                />
              </div>
            </div>
          </div>

          {/* LEFT SIDE - FORM */}
          <div className="col-lg-5 col-md-12">
            <div className="d-flex align-items-center justify-content-center vh-100 bg-white px-4">
              <div style={{ width: "100%", maxWidth: "440px" }}>
                <div className="mx-auto mb-3 text-center">
                  <img
                    src={logo}
                    className="img-fluid"
                    alt="Logo"
                    style={{ maxWidth: "170px" }}
                  />
                </div>

                <div className="forgot-card">
                  <form>
                    <div className="text-center mb-3">
                      <h2 className="forgot-title mb-2">Password Reset</h2>
                      <p className="forgot-subtext mb-0">
                        Please set your password as it is mandatory to change
                      </p>
                      <p className="forgot-subtext mb-0">
                        after first login for security purpose.
                      </p>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Email Address{" "}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="Enter Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Password <span className="text-danger ms-1">*</span>
                      </label>
                      <div className="pass-group" id="passwordInput">
                        <input
                          type={
                            passwordVisibility.password ? "text" : "password"
                          }
                          value={newPassword}
                          onChange={(e) => {
                            setNewPassword(e.target.value);
                            onChangePassword(e.target.value);
                          }}
                          className="form-control pass-input"
                          placeholder="Enter your password"
                        />
                        <span
                          className={`ti toggle-passwords ${
                            passwordVisibility.password
                              ? "ti-eye"
                              : "ti-eye-off"
                          }`}
                          onClick={() => togglePasswordVisibility("password")}
                          style={{ cursor: "pointer" }}
                        ></span>
                      </div>

                      <div className="password-rules mt-2">
                        <p
                          className={`m-0 d-flex align-items-center gap-2 ${
                            password.length >= 12 && password.length <= 15
                              ? "text-success"
                              : "text-secondary"
                          }`}
                        >
                          <i className="ti ti-check"></i>
                          Min length is 12 and max length is 15.
                        </p>

                        <p
                          className={`m-0 d-flex align-items-center gap-2 ${
                            /[A-Z]/.test(password) && /[a-z]/.test(password)
                              ? "text-success"
                              : "text-secondary"
                          }`}
                        >
                          <i className="ti ti-check"></i>
                          At least a uppercase and a lowercase letter.
                        </p>

                        <p
                          className={`m-0 d-flex align-items-center gap-2 ${
                            /[0-9]/.test(password)
                              ? "text-success"
                              : "text-secondary"
                          }`}
                        >
                          <i className="ti ti-check"></i>
                          At least include a digit number.
                        </p>

                        <p
                          className={`m-0 d-flex align-items-center gap-2 ${
                            /[#?!@$%^&*-]/.test(password)
                              ? "text-success"
                              : "text-secondary"
                          }`}
                        >
                          <i className="ti ti-check"></i>
                          At least a special characters.
                        </p>
                      </div>

                      <p className="fs-12 mt-1 mb-0">
                        {passwordResponce.passwordResponceText}
                      </p>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Confirm Password{" "}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <div className="pass-group">
                        <input
                          type={
                            passwordVisibility.confirmPassword
                              ? "text"
                              : "password"
                          }
                          className="form-control pass-input"
                          placeholder="Confirm Password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />

                        <span
                          className={`ti toggle-passwords${
                            passwordVisibility.confirmPassword
                              ? "ti-eye"
                              : "ti-eye-off"
                          }`}
                          onClick={() =>
                            togglePasswordVisibility("confirmPassword")
                          }
                          style={{ cursor: "pointer" }}
                        ></span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleResetPassword}
                      >
                        Change Password
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-primary w-100 mt-2"
                        onClick={() => navigate(-1)}
                      >
                        <i className="ti ti-arrow-left me-2"></i>
                        Back
                      </button>
                    </div>

                    <p className="mb-0">
                      In case you are facing any issue write to us at: <br />
                      <Link
                        to={"mailto:candidatesupport@aupertz.co.in"}
                        className="text-danger"
                      >
                        candidatesupport@aupertz.co.in
                      </Link>
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
